import { API } from '../Auth';

// Data Graph — the platform proxies the discover worker's graph API 1:1 under
// /api/v1/graph/data-sources/{platformDataSourceId}/… (contract: c2-discover-api/docs/GRAPH_API.md).
// Everything is served off the source's LATEST Profile snapshot; a 404 means "run Profile first".
//
// A data source has any number of NAMED data graphs (/api/v1/graph/graphs, 2026-10-30): each a key,
// a hop limit, and its own manual links and edge verdicts. `graphId` picks one; a read without it
// sees the base structure (declared keys only, no manual links), and every link / verdict write
// needs it. Consultants set graphs up in the operator console; a TDM subset source picks one.
const EP = 'discoverRuns';
const base = (id) => `/api/v1/graph/data-sources/${encodeURIComponent(id)}`;
const GRAPHS = '/api/v1/graph/graphs';
// C2API sends query params on GET only; a write carries its graphId in the path.
const withGraph = (path, graphId) => (graphId != null && graphId !== '' ? `${path}?graphId=${encodeURIComponent(graphId)}` : path);
const unwrap = (res) => (res && res.data !== undefined && res.status !== undefined ? res.data : res);
const qs = (params) => {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => { if (v != null && v !== '') out[k] = String(v); });
  return Object.keys(out).length ? { queryStringParameters: out } : undefined;
};

/** True when the error means the source has no Profile snapshot yet (404 GRAPH_NOT_AVAILABLE). */
export const isGraphUnavailable = (e) => Boolean(e && Number(e.status) === 404);

/**
 * Every named data graph, or one data source's: `{ id, dataSourceId, dataSourceName, name, keyName, keyTable, keyColumn,
 * maxHops, outcome, objects, starts, lastTest, tableFilters, warnings[], usedBy[] }` (contract 3.0: `outcome` CONNECTED |
 * LIST, `objects` = the list's objects with their rules; 3.1: each rule's `origin` GIVEN | DERIVED, `starts` =
 * [{ table, column }] extra starts, `lastTest` = { state, startedAt, finishedAt, snapshotId, errors, warnings } or null
 * when the graph has not been tested since it last changed).
 */
export async function listDataGraphs({ dataSourceId } = {}) {
  const res = unwrap(await API.get(EP, GRAPHS, qs({ dataSourceId })));
  return Array.isArray(res) ? res : [];
}

export async function getDataGraph(graphId) {
  return unwrap(await API.get(EP, `${GRAPHS}/${encodeURIComponent(graphId)}`));
}

/**
 * Admin. `{ dataSourceId, name, description, keyName, keyTable, keyColumn, maxHops, tableFilters, outcome, objects }`; 409
 * on a duplicate name. A null `tableFilters` / `objects` keeps what is stored.
 */
export async function createDataGraph(body) {
  return unwrap(await API.post(EP, GRAPHS, { body }));
}

/** Admin. Same body; the data source cannot change. */
export async function updateDataGraph(graphId, body) {
  return unwrap(await API.put(EP, `${GRAPHS}/${encodeURIComponent(graphId)}`, { body }));
}

/** Admin. 409 TDM_IN_USE while a subset source uses the graph. Its manual links and verdicts go with it. */
export async function deleteDataGraph(graphId) {
  return API.del(EP, `${GRAPHS}/${encodeURIComponent(graphId)}`);
}

/**
 * Test a data graph (2026-11-01): plan it from its key table and walk it for a few records — no
 * subset, nothing staged or masked. body = { mode: ANY | WHERE | SEEDS, where, seeds }: ANY and WHERE
 * take 10 records, SEEDS the first 100 ids. Answers 202 with the status getGraphTest returns. Not
 * admin-only (it changes nothing); a READONLY account is refused every non-GET.
 */
export async function startGraphTest(graphId, body) {
  return unwrap(await API.post(EP, `${GRAPHS}/${encodeURIComponent(graphId)}/test`, { body }));
}

/**
 * The graph's last test: `{ state: NONE | RUNNING | SUCCEEDED | FAILED, jobId, startedAt, finishedAt,
 * message, request: { mode, where, seedsCount }, report, probe, progress }`. Any change to the graph clears it.
 * While RUNNING (the POST's 202 too) `report` and `probe` are null and `progress` = { phase (SEEDS |
 * EXTRACT), currentItem, tablesDone, tablesTotal, rows, percent }, each key absent until the worker
 * reports it; once finished, `report` (+ `probe` on success) and no `progress`.
 */
export async function getGraphTest(graphId) {
  return unwrap(await API.get(EP, `${GRAPHS}/${encodeURIComponent(graphId)}/test`));
}

/**
 * The isolation report of a DRAFT (contract 3.0): body = the editor's DataGraphRequest + `graphId` (whose manual
 * links and verdicts apply). Built from the plan only (`counted: false`): what "Needed by this list" shows before
 * the Test. Any signed-in user; it writes nothing.
 */
export async function getDraftReport(body) {
  const res = unwrap(await API.post(EP, `${GRAPHS}/report`, { body }));
  // the report itself; tolerate a { report } wrapper
  return res && res.report && !res.objects ? res.report : res;
}

/**
 * Candidate rules per listed object, ranked (contract 3.0): body as getDraftReport. Answers
 * `{ suggestions: [{ object, candidates: [{ column, source, sourceColumn, kind, via, edgeId, polymorphic, sourceHop, rank, reason }] }] }`,
 * names in display form. `kind` = CHILD_LINK (the object looks the source up) | PARENT_LINK (the source looks the
 * object up) | SAME_PARENT (both look up `via`).
 */
export async function getRuleSuggestions(body) {
  const res = unwrap(await API.post(EP, `${GRAPHS}/rule-suggestions`, { body }));
  return (res && Array.isArray(res.suggestions)) ? res.suggestions : [];
}

export async function getGraphSummary(dataSourceId, { graphId } = {}) {
  return unwrap(await API.get(EP, `${base(dataSourceId)}/summary`, qs({ graphId })));
}

/** filter = all | connected | isolated; sort = name | edges | rows; dir = asc | desc. */
export async function getGraphNodes(dataSourceId, { q, filter, schema, page, size, sort, dir, graphId } = {}) {
  return unwrap(await API.get(EP, `${base(dataSourceId)}/nodes`, qs({ q, filter, schema, page, size, sort, dir, graphId })));
}

/** The worker clamps a nodes/edges page to this many rows (GraphService.MAX_PAGE_SIZE). */
export const GRAPH_MAX_PAGE = 1000;

/**
 * Every node of the graph, paging at the worker's cap until the total is reached. A Salesforce
 * org can carry more than one page of data objects once custom objects pile up, and the object
 * list / network view need the whole set in memory (per-neighbour record counts come from it).
 * A 404 propagates unchanged so `isGraphUnavailable` still answers "run Profile first".
 */
export async function getAllGraphNodes(dataSourceId, { q, filter, schema, graphId } = {}) {
  const items = [];
  let total = 0;
  for (let page = 0; ; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await getGraphNodes(dataSourceId, { q, filter, schema, page, size: GRAPH_MAX_PAGE, graphId });
    const batch = (res && res.items) || [];
    items.push(...batch);
    total = res && res.total != null ? Number(res.total) : items.length;
    if (batch.length === 0 || items.length >= total) break;
  }
  return { items, total };
}

/** Node identity, the same rule the worker's TableRef.key uses: schema (empty for Salesforce) + table. */
export const graphNodeKey = (ref) => (ref ? `${ref.schema || ''}${ref.table || ''}` : '');

export async function getGraphNode(dataSourceId, table, schema, graphId) {
  return unwrap(await API.get(EP, `${base(dataSourceId)}/nodes/${encodeURIComponent(table)}`, qs({ schema, graphId })));
}

export async function getGraphNeighbors(dataSourceId, table,
  { schema, hops = 3, direction = 'both', includeManual = true, includeInferred = true, minConfidence, graphId } = {}) {
  return unwrap(await API.get(EP, `${base(dataSourceId)}/nodes/${encodeURIComponent(table)}/neighbors`,
    qs({ schema, hops, direction, includeManual, includeInferred, minConfidence, graphId })));
}

export async function getGraphEdges(dataSourceId, { origin, status, table, page, size, graphId } = {}) {
  return unwrap(await API.get(EP, `${base(dataSourceId)}/edges`, qs({ origin, status, table, page, size, graphId })));
}

/**
 * Candidate manual links the schema does not declare, ranked by confidence. Read-only — a row's
 * own `from` / `fromColumns` / `to` / `toColumns` are already in createManualLink's request shape.
 * `table` scopes to suggestions touching that table on either side.
 */
export async function getLinkSuggestions(dataSourceId, { table, schema, minConfidence, limit, graphId } = {}) {
  return unwrap(await API.get(EP, `${base(dataSourceId)}/link-suggestions`,
    qs({ table, schema, minConfidence, limit, graphId })));
}

/** Admin. status = ACTIVE | CONFIRMED | REJECTED, in one data graph. Only declared / inferred edges (numeric ids) take a verdict. */
export async function setEdgeStatus(dataSourceId, graphId, edgeId, status) {
  return unwrap(await API.patch(EP, withGraph(`${base(dataSourceId)}/edges/${encodeURIComponent(edgeId)}/status`, graphId),
    { body: { status } }));
}

/** One data graph's manual links; [] without a graph. */
export async function listManualLinks(dataSourceId, graphId) {
  const res = unwrap(await API.get(EP, `${base(dataSourceId)}/manual-links`, qs({ graphId })));
  return Array.isArray(res) ? res : [];
}

/** Admin. `from` is the referencing (child) side, `to` the referenced (parent) side. createdBy is stamped server-side. */
export async function createManualLink(dataSourceId, graphId, { fromSchema, fromTable, fromColumns, toSchema, toTable, toColumns, note }) {
  return unwrap(await API.post(EP, withGraph(`${base(dataSourceId)}/manual-links`, graphId), {
    body: { fromSchema, fromTable, fromColumns, toSchema, toTable, toColumns, note },
  }));
}

/** Admin. */
export async function deleteManualLink(dataSourceId, graphId, linkId) {
  return API.del(EP, withGraph(`${base(dataSourceId)}/manual-links/${encodeURIComponent(linkId)}`, graphId));
}

/** Traversal closure — the plan c2-tdm-service executes for a subset. */
export async function getGraphPlan(dataSourceId, request) {
  return unwrap(await API.post(EP, `${base(dataSourceId)}/plan`, { body: request }));
}

export const tableLabel = (ref) => (ref ? (ref.schema ? `${ref.schema}.${ref.table}` : ref.table || '') : '');

export const EDGE_KIND_LABEL = {
  FK: 'FK', LOOKUP: 'LOOKUP', MASTER_DETAIL: 'MASTER-DETAIL', MANUAL: 'MANUAL', INFERRED: 'INFERRED',
};
export const edgeKindLabel = (kind) => EDGE_KIND_LABEL[kind] || kind || '';

export const GRAPH_SOURCE_LABEL = {
  JDBC_CATALOG: 'Declared keys from the catalog',
  SALESFORCE_DESCRIBE: 'Describe referenceTo',
  NONE: 'No declared keys',
};
export const graphSourceLabel = (s) => GRAPH_SOURCE_LABEL[s] || (s ? s : 'Unknown');

/**
 * The sources a data graph can be set up for — the ones a TDM subset can pull from: relational,
 * MongoDB (its profile snapshot gives collections; links come from manual links and the shared key)
 * and Salesforce. Platform-managed rows (staging areas, raw views) never.
 */
export const GRAPH_CAPABLE_TYPES = ['RELATIONAL', 'SAAS', 'NOSQL'];
const GRAPH_CAPABLE_ENGINES = { NOSQL: ['mongodb'], SAAS: ['salesforce'] };
export const isGraphCapable = (connection) => {
  if (!connection || connection.ownerRef || !GRAPH_CAPABLE_TYPES.includes(connection.datasourceType)) return false;
  const engines = GRAPH_CAPABLE_ENGINES[connection.datasourceType];
  return !engines || engines.includes(String(connection.type || connection.engine || '').toLowerCase());
};

/** MongoDB and Salesforce walk without a hop limit; only a relational graph has one. */
export const graphHasHopLimit = (connection) => Boolean(connection) && connection.datasourceType !== 'NOSQL'
  && connection.datasourceType !== 'SAAS';

/** The planner's default when a relational graph sets no limit (TdmStartPayloadFactory.DEFAULT_MAX_HOPS). */
export const DEFAULT_MAX_HOPS = 3;

/** "3", "3 (default)" or "No limit" (MongoDB and Salesforce). `source` falls back to the graph record's own datasourceType. */
export const graphHopsLabel = (g, source) => {
  if (!graphHasHopLimit(source || { datasourceType: g && g.datasourceType })) return 'No limit';
  return g && g.maxHops != null ? String(g.maxHops) : `${DEFAULT_MAX_HOPS} (default)`;
};

/**
 * Row filters (contract 2.1): `graph.tableFilters` = [{ table, expression }], at most one per table,
 * in the source's own language. The TDM worker ANDs each into every query of its table, so rows a
 * filter leaves out are never pulled, not even as a parent.
 */
export const rowFilterMap = (graph) => {
  const out = {};
  ((graph && graph.tableFilters) || []).forEach((f) => { if (f && f.table) out[String(f.table).toLowerCase()] = f.expression; });
  return out;
};
export const rowFilterFor = (graph, table) => rowFilterMap(graph)[String(table || '').toLowerCase()] || null;

/** The graph's filter list with `table`'s replaced; a blank expression removes it. */
export const withRowFilter = (graph, table, expression) => {
  const key = String(table || '').toLowerCase();
  const rest = ((graph && graph.tableFilters) || []).filter((f) => String(f.table || '').toLowerCase() !== key);
  const e = String(expression || '').trim();
  return e ? [...rest, { table, expression: e }] : rest;
};

/**
 * A PUT body that changes only the row filters (the platform replaces every other field from the body; a
 * null `objects` keeps the stored list).
 */
export const graphUpdateBody = (graph, tableFilters, objects = graph.objects) => ({
  dataSourceId: graph.dataSourceId, name: graph.name, description: graph.description || null, keyName: graph.keyName || null,
  keyTable: graph.keyTable || null, keyColumn: graph.keyColumn || null, maxHops: graph.maxHops, tableFilters,
  outcome: graphOutcome(graph),
  objects: Array.isArray(objects) ? objects : null,
  starts: Array.isArray(graph.starts) ? graph.starts : null,
});

// ---- outcomes, objects and rules (contract 3.0, plan: docs/test-data-subsetter/data-graph-scopes-plan-2026-11-03.md) ----
//
// A data graph asks for one of two things. CONNECTED: everything connected to the key table (child links,
// recursively, to the hop limit, plus what those rows reference) — the walk subsets always had. LIST: these
// objects, each reached by one or more RULES `Object.column IN (Source.column)` (Source = the key table or
// another listed object), plus referential integrity. An object with no rule is reference-only: integrity
// brings its rows. `save: false` = "use, don't save": its rows feed rules and are never staged.

export const OUTCOMES = [['CONNECTED', 'Everything connected'], ['LIST', 'A list of objects']];
export const graphOutcome = (g) => (g && g.outcome === 'LIST' ? 'LIST' : 'CONNECTED');

/** `Opportunity.ContactId IN Contact.Id`: the rule's id, the same text the planner and the worker use. */
export const ruleText = (object, rule) => (rule
  ? `${object}.${rule.column} IN ${rule.source}.${rule.sourceColumn}`
  : '');

const sameName = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();
export const objectIndex = (objects, name) => (objects || []).findIndex((o) => sameName(o && o.name, name));

/** The list with `name` added as a reference-only object (no rules, saved); unchanged when it is already listed. */
export const withObject = (objects, name) => {
  const list = Array.isArray(objects) ? objects : [];
  const n = String(name || '').trim();
  if (!n || objectIndex(list, n) >= 0) return list;
  return [...list, { name: n, save: true, rules: [] }];
};

/** The list without `name`; rules elsewhere that read it as their source go too (the platform 400s them). */
export const withoutObject = (objects, name) => (objects || [])
  .filter((o) => !sameName(o.name, name))
  .map((o) => ({ ...o, rules: (o.rules || []).filter((r) => !sameName(r.source, name)) }));

/** The object's rules with `rule` added; a rule it already has is not added twice. */
export const withRule = (objects, name, rule) => (objects || []).map((o) => {
  if (!sameName(o.name, name)) return o;
  const rules = o.rules || [];
  return rules.some((r) => ruleText(o.name, r) === ruleText(o.name, rule)) ? o : { ...o, rules: [...rules, rule] };
});

// Rule origins (3.1): GIVEN = the user's; DERIVED = the planner's pick for an object that had none, written
// onto the graph by its Test — a default to confirm or change, never silently final. (A START rule only ever
// appears in a report: it is an extra start the platform expanded.)
export const isDerived = (rule) => Boolean(rule) && rule.origin === 'DERIVED';

/** The object's rules with the user's `rule` in place of its DERIVED one (or added, when it has none). */
export const withChosenRule = (objects, name, rule) => (objects || []).map((o) => {
  if (!sameName(o.name, name)) return o;
  const chosen = { ...rule, origin: 'GIVEN' };
  const rules = o.rules || [];
  if (rules.some((r) => ruleText(o.name, r) === ruleText(o.name, chosen))) {
    return { ...o, rules: rules.map((r) => (ruleText(o.name, r) === ruleText(o.name, chosen) ? chosen : r)).filter((r) => !isDerived(r)) };
  }
  const i = rules.findIndex(isDerived);
  return { ...o, rules: i < 0 ? [...rules, chosen] : rules.map((r, j) => (j === i ? chosen : r)) };
});

/** The object's DERIVED rule at `index` kept as the user's own. */
export const withConfirmedRule = (objects, name, index) => (objects || []).map((o) => (sameName(o.name, name)
  ? { ...o, rules: (o.rules || []).map((r, j) => (j === index ? { ...r, origin: 'GIVEN' } : r)) }
  : o));

/**
 * A finding's fix applied to the list (3.1): TICK lists `fix.table` (reference-only; the Test derives its
 * rule); RULE adds `fix.object.fix.column IN fix.source.fix.sourceColumn` to `fix.object` (listed first if it
 * is not), in place of a derived rule when it has one.
 */
export const applyFix = (objects, fix) => {
  if (!fix) return objects || [];
  if (fix.action === 'TICK') return withObject(objects, fix.table);
  if (fix.action === 'RULE') {
    return withChosenRule(withObject(objects, fix.object), fix.object,
      { column: fix.column, source: fix.source, sourceColumn: fix.sourceColumn, maxPerParent: null });
  }
  return objects || [];
};

/** Why the list cannot be saved yet, or null: the platform's own 400s, said before the save. */
export const objectsProblem = (objects, keyTable) => {
  const list = objects || [];
  const seen = new Set();
  for (let i = 0; i < list.length; i += 1) {
    const o = list[i];
    const n = String((o && o.name) || '').trim();
    if (!n) return 'Every listed object needs a name.';
    if (seen.has(n.toLowerCase())) return `${n} is listed twice.`;
    seen.add(n.toLowerCase());
  }
  for (let i = 0; i < list.length; i += 1) {
    const o = list[i];
    for (let j = 0; j < (o.rules || []).length; j += 1) {
      const r = o.rules[j];
      if (!String(r.column || '').trim() || !String(r.sourceColumn || '').trim()) return `A rule of ${o.name} is missing a column.`;
      if (!sameName(r.source, keyTable) && !seen.has(String(r.source || '').toLowerCase())) {
        return `${ruleText(o.name, r)}: ${r.source} is neither the key table nor a listed object.`;
      }
      if (r.maxPerParent != null && !(Number.isInteger(Number(r.maxPerParent)) && Number(r.maxPerParent) >= 1)) {
        return `${ruleText(o.name, r)}: "at most" is a whole number of 1 or more.`;
      }
    }
  }
  return null;
};

/** The record types a filter can choose from: Describe's list for the object, without the Master default. */
export const pickableRecordTypes = (recordTypes) => (recordTypes || []).filter((rt) => rt && !rt.master && rt.developerName);

const soqlLiteral = (v) => `'${String(v).replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;

/**
 * SOQL keeping only these record types. DeveloperName, not Name: the name is the label, translated
 * into the integration user's language (NPSP's "Household Account" is `HH_Account`).
 */
export const recordTypeFilter = (developerNames) => {
  const names = (developerNames || []).filter(Boolean);
  if (names.length === 0) return '';
  return names.length === 1
    ? `RecordType.DeveloperName = ${soqlLiteral(names[0])}`
    : `RecordType.DeveloperName IN (${names.map(soqlLiteral).join(', ')})`;
};

/** The developer names a recordTypeFilter() expression selects; null when the expression is anything else. */
export const recordTypesInFilter = (expression) => {
  const e = String(expression || '').trim();
  if (!e) return [];
  const one = e.match(/^RecordType\.DeveloperName\s*=\s*'((?:[^'\\]|\\.)*)'$/i);
  if (one) return [one[1].replace(/\\(.)/g, '$1')];
  const many = e.match(/^RecordType\.DeveloperName\s+IN\s*\((.*)\)$/i);
  if (!many) return null;
  const literal = /'((?:[^'\\]|\\.)*)'/g;
  const names = [];
  let m;
  while ((m = literal.exec(many[1])) !== null) names.push(m[1].replace(/\\(.)/g, '$1'));
  // nothing but the quoted names and their commas, or it is a hand-written filter
  return names.length > 0 && many[1].replace(literal, '').replace(/[\s,]/g, '') === '' ? names : null;
};

/** "Member number · public.person.person_source_value", or null when the graph has no key. */
export const graphKeyLabel = (g) => {
  if (!g || !g.keyTable) return g && g.keyName ? g.keyName : null;
  const where = `${g.keyTable}${g.keyColumn ? `.${g.keyColumn}` : ''}`;
  return g.keyName ? `${g.keyName} · ${where}` : where;
};
